import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const plans = [
  {
    name: 'MVP Sprint',
    price: '$25k – $75k',
    timeline: '4 – 8 weeks',
    team: '2 – 4 engineers',
    description: 'A focused, fixed-scope build to validate your idea, ship a v1, or secure your next funding round.',
    highlight: false,
    features: [
      'Product discovery & wireframes',
      'Core feature set (1–3 flows)',
      'Responsive web or mobile app',
      'CI/CD pipeline setup',
      'QA & launch support',
      '30-day post-launch warranty',
    ],
    roi: {
      label: 'Avg. faster to market',
      value: '40%',
    },
    accentColor: 'bg-sky-500',
    accentBg: 'bg-sky-50',
    accentBorder: 'border-sky-200',
    accentText: 'text-sky-600',
  },
  {
    name: 'Growth Build',
    price: '$75k – $250k',
    timeline: '3 – 6 months',
    team: '4 – 8 engineers',
    description: 'A full product build with design systems, advanced features, integrations, and scalable architecture.',
    highlight: true,
    features: [
      'Full UI/UX design system',
      'Custom web, mobile & API layer',
      'Third-party integrations',
      'Cloud infrastructure (AWS/GCP)',
      'Automated testing & monitoring',
      'Dedicated product manager',
      'Bi-weekly sprint demos',
    ],
    roi: {
      label: 'Avg. cost reduction vs. in-house',
      value: '35%',
    },
    accentColor: 'bg-primary-500',
    accentBg: 'bg-primary-50',
    accentBorder: 'border-primary-200',
    accentText: 'text-primary-600',
  },
  {
    name: 'Enterprise Platform',
    price: '$250k+',
    timeline: '6 – 12 months',
    team: '8 – 15 engineers',
    description: 'Complex multi-tenant platforms, AI pipelines, legacy modernization, or regulated-industry products.',
    highlight: false,
    features: [
      'Multi-tenant architecture',
      'AI/ML & data pipeline engineering',
      'Enterprise security & compliance',
      'Microservices & event-driven design',
      'DevOps & SRE operations',
      'Dedicated squad with Tech Lead',
      'Ongoing maintenance & support',
    ],
    roi: {
      label: 'Avg. uptime SLA achieved',
      value: '99.9%',
    },
    accentColor: 'bg-navy-900',
    accentBg: 'bg-gray-50',
    accentBorder: 'border-gray-200',
    accentText: 'text-navy-900',
  },
];

const trustBadges = [
  { icon: 'ri-time-line', label: 'Fixed timeline guarantee', desc: 'Every plan includes a committed delivery window.' },
  { icon: 'ri-coins-line', label: 'Transparent milestone billing', desc: 'Pay per sprint or milestone — no surprise invoices.' },
  { icon: 'ri-loop-left-line', label: 'Scope-change protocol', desc: 'Agile change requests with upfront impact assessment.' },
  { icon: 'ri-shield-check-line', label: 'IP ownership transfer', desc: 'Full source code and IP rights handed over on delivery.' },
];

export default function DevPricingSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-coins-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Transparent Pricing</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Investment Plans &
            <span className="text-primary-600"> ROI</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            No hidden fees. Every plan includes architecture, design, engineering, QA, and deployment — with clear timelines and measurable returns.
          </motion.p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-12 md:mb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: 0.2 + index * 0.1, ...springTransition }}
              className={`group relative bg-white rounded-2xl border ${plan.highlight ? 'border-primary-300 shadow-xl shadow-primary-500/5' : 'border-gray-100'} hover:shadow-xl transition-all duration-500 overflow-hidden flex flex-col`}
            >
              {/* Top accent bar */}
              <div className={`h-1.5 w-full ${plan.accentColor}`}></div>

              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary-500 text-white text-xs font-bold rounded-full shadow-md">
                  Most Popular
                </div>
              )}

              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <div className="mb-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{plan.name}</span>
                </div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-3xl md:text-4xl font-bold text-navy-900">{plan.price}</span>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed mb-6">{plan.description}</p>

                {/* Meta row */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line text-gray-400 text-sm"></i>
                    <span className="text-xs font-semibold text-gray-600">{plan.timeline}</span>
                  </div>
                  <div className="w-px h-4 bg-gray-200"></div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-group-line text-gray-400 text-sm"></i>
                    <span className="text-xs font-semibold text-gray-600">{plan.team}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <div key={f} className="flex items-start gap-2.5">
                      <div className={`w-5 h-5 flex items-center justify-center rounded-full ${plan.accentBg} flex-shrink-0 mt-0.5`}>
                        <i className={`ri-check-line text-[10px] ${plan.accentText}`}></i>
                      </div>
                      <span className="text-sm text-gray-600">{f}</span>
                    </div>
                  ))}
                </div>

                {/* ROI badge */}
                <div className={`${plan.accentBg} border ${plan.accentBorder} rounded-xl p-3 mb-6`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">{plan.roi.label}</span>
                    <span className={`text-lg font-bold ${plan.accentText}`}>{plan.roi.value}</span>
                  </div>
                </div>

                <a
                  href="#cta"
                  className={`block text-center py-3 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer ${plan.highlight ? 'bg-primary-500 text-white hover:shadow-lg hover:shadow-primary-500/25 hover:-translate-y-0.5' : 'bg-gray-50 text-navy-900 hover:bg-gray-100 border border-gray-200'}`}
                >
                  Request a Roadmap
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust badges row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, ...springTransition }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10 md:mb-12"
        >
          {trustBadges.map((badge) => (
            <div key={badge.label} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
              <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-gray-100 flex-shrink-0">
                <i className={`${badge.icon} text-primary-600 text-sm`}></i>
              </div>
              <div>
                <div className="text-sm font-bold text-navy-900">{badge.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{badge.desc}</div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7, ...springTransition }}
          className="text-center"
        >
          <p className="text-sm text-gray-500 mb-4">
            Not sure which plan fits? We will scope your project and recommend the right approach — no obligation.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors group cursor-pointer"
          >
            View detailed pricing breakdown
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}